import React, { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	TextField,
	Typography,
	Stack,
	Grid2,
	TablePagination,
	IconButton,
	InputAdornment,
	CircularProgress,
} from "@mui/material";
import { toast } from "sonner";
import { Iconify } from "../Minimal/src/components/iconify";
import { Link } from 'react-router-dom';
import { API_URL, BASE_URL } from '../Config';
import config from '../common/Config';
import PurchaseService from '../../services/PurchaseService';

const PurchasesHistory = () => {
	const [purchaseGrn, setPurchaseGrn] = useState([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [searchQuery, setSearchQuery] = useState("");
	const [draw, setDraw] = useState(1);
  const token = localStorage.getItem("token");
	const accessToken = localStorage.getItem('accessToken');
	const company_tax_type = localStorage.getItem("company_tax_type");

	const fetchPurchaseGrnList = useCallback(async () => {
		if (!accessToken) {
			window.location.href = `${config.BASE_URL}/login`;
			return;
		}

		setLoading(true);

		try {
			const data = await PurchaseService.fetchPurchaseGrnList(draw, 0, 10000, token);
			setPurchaseGrn(Array.isArray(data) ? data : []);
		} catch (error) {
			toast.error('Failed to load purchase GRN list');
			setPurchaseGrn([]);
		} finally {
			setLoading(false);
		}
	}, [draw, accessToken]);

	useEffect(() => {
		fetchPurchaseGrnList();
	}, [fetchPurchaseGrnList]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearchChange = (e) => {
		setSearchQuery(e.target.value);
		setPage(0);
	};

	const filteredData = purchaseGrn.filter((row) => {
		const searchLower = searchQuery.toLowerCase();
		return (
			(row.date && row.date.toLowerCase().includes(searchLower)) ||
			(row.Po && row.Po.toString().toLowerCase().includes(searchLower)) ||
			(row.Grn && row.Grn.toString().toLowerCase().includes(searchLower)) ||
			(row.invoice_number && row.invoice_number.toLowerCase().includes(searchLower)) ||
			(row.supplier && row.supplier.toLowerCase().includes(searchLower))
		);
	});

	const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

	return (
		<>
		<Box className="page-container">
				<Paper className="paper-style">
					<Box sx={{ px: 2, py: 1.2 }}>
						<Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<Typography className="page-title">
									GRN
								</Typography>
							</Grid2>
							<Grid2 size={{ xs: 12, md: 4 }}>
								<TextField
									fullWidth
									className="input-border"
									size="small"
									placeholder="Search..."
									value={searchQuery}
									onChange={handleSearchChange}
									slotProps={{
										input: {
											startAdornment: (
												<InputAdornment position="start">
													<Iconify icon="eva:search-outline"/>
												</InputAdornment>
											),
										},
									}}
								/>
							</Grid2>
						</Grid2>
					</Box>
					<TableContainer>
						<Table size="medium" 
						className="table-cell-border" 
						sx={{tableLayout: "fixed"}}>
							<TableHead>
								<TableRow className="table-cell-header">
									<TableCell
										sx={{
											width: "100px",
											minWidth: "100px",
										}}
									>
										Date
									</TableCell>
									<TableCell
										sx={{
											width: "100px",
											minWidth: "100px",
										}}
									>
										PO No
									</TableCell>
									<TableCell
										sx={{
											width: "100px",
											minWidth: "100px",
										}}
									>
										Grn No
									</TableCell>
									<TableCell
										sx={{
											width: "120px",
											minWidth: "120px",
										}}
									>
										Supplier Inv No
									</TableCell>
									<TableCell
										sx={{
											width: "200px",
											minWidth: "200px",
										}}
									>
										Supplier
									</TableCell>
									<TableCell
										sx={{
											width: "100px",
											minWidth: "100px",
											textAlign: "right",
										}}
									>
										{company_tax_type}
									</TableCell>
									<TableCell
										sx={{
											width: "120px",
											minWidth: "120px",
											textAlign: "right",
										}}
									>
										Grand Total
									</TableCell>
									<TableCell
										sx={{
											width: "80px",
											minWidth: "80px",
											textAlign: "center",
										}}
									>
										Action
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={8} sx={{ textAlign: "center", py: 3 }}>
											<CircularProgress size={24} />
										</TableCell>
									</TableRow>
								) : paginatedData.length === 0 ? (
									<TableRow>
										<TableCell colSpan={8} sx={{ textAlign: "center", py: 3 }}>
											No data available
										</TableCell>
									</TableRow>
								) : (
									paginatedData.map((row, index) => (
										<TableRow
											key={index}
											className="table-cell-style"
											hover
											sx={{
												"&:hover": {
													backgroundColor: "rgba(145, 158, 171, 0.04)"
												},
												transition: "background-color 0.2s ease"
											}}
										>
											<TableCell>
												{row.date || ""}
											</TableCell>
											<TableCell>
												{row.Po || ""}
											</TableCell>
											<TableCell>
												{row.Grn || ""}
											</TableCell>
											<TableCell>
												{row.invoice_number || ""}
											</TableCell>
											<TableCell>
												{row.supplier || ""}
											</TableCell>
											<TableCell
											sx={{
												textAlign: "right",
												fontWeight: 500,
											}}
											>
												{row.total_tax || "0.00"}
											</TableCell>
											<TableCell
												sx={{
													textAlign: "right",
													fontWeight: 500,
												}}
											>
												{row.grand_total || "0.00"}
											</TableCell>
											<TableCell
											sx={{
													textAlign: "center",
												}}
											>
												<IconButton
													component={Link}
													to={`${BASE_URL}/purchases/view_grn/${row.id}`}
													size="small"
													sx={{
														color: "#637381",
														"&:hover": {
															backgroundColor: "rgba(33, 43, 54, 0.08)",
															color: "#212b36",
														},
													}}
												>
													<Iconify icon="eva:eye-outline" width={20} />
												</IconButton>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
				</TableContainer>

				<Box 
					sx={{ 
						display: "flex", 
						justifyContent: "flex-end", 
						alignItems: "center" 
					}}
				>
					<TablePagination
						component="div"
						count={filteredData.length}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[10, 20, 50]}
						className="pagination-styles"
						labelDisplayedRows={({ from, to, count }) => {
							if (count === 0) return "0-0 of 0";
							return `${from}-${to} of ${count}`;
						}}
					
					/>
				</Box>
				</Paper>
			</Box>

		</>
	);
}
export default PurchasesHistory;